/**
 * The IndexedDB store (tech-design §5): DB `incrementus` with three object stores —
 * `saves` (one envelope per slot, 3 slots), `backups` (a per-slot ring of the last
 * {@link MAX_BACKUPS_PER_SLOT} autosaves), and `settings` (small key/value prefs).
 *
 * This is the only module here that does I/O; the envelope/codec/migration logic it
 * builds on is pure and independently tested. On a corrupt slot, {@link loadFromSlot}
 * walks the backup ring newest→oldest until one decodes — a save is never silently
 * lost.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { createInitialState, type GameState } from '@core/index.js';
import { encodeSave, tryDecode, type SaveEnvelope } from './envelope.js';

export const SLOTS = ['slot-1', 'slot-2', 'slot-3'] as const;
export type Slot = (typeof SLOTS)[number];
export const DEFAULT_SLOT: Slot = 'slot-1';

/** Per-slot backup ring depth (tech-design §5: "last 20 autosaves per slot"). */
export const MAX_BACKUPS_PER_SLOT = 20;

const DB_NAME = 'incrementus';
const DB_VERSION = 2;

interface BackupRecord {
  /** Injected by the autoIncrement key path; ascending id ⇒ chronological order. */
  id?: number;
  readonly slot: string;
  readonly savedAt: number;
  readonly envelope: SaveEnvelope;
}

interface IncrementusDb extends DBSchema {
  saves: { key: string; value: SaveEnvelope };
  backups: {
    key: number;
    value: BackupRecord;
    indexes: { 'by-slot': string };
  };
  settings: { key: string; value: unknown };
}

function openGameDb(): Promise<IDBPDatabase<IncrementusDb>> {
  return openDB<IncrementusDb>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('saves')) {
        db.createObjectStore('saves');
      }
      if (!db.objectStoreNames.contains('backups')) {
        const backups = db.createObjectStore('backups', {
          keyPath: 'id',
          autoIncrement: true,
        });
        backups.createIndex('by-slot', 'slot');
      }
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings');
      }
    },
  });
}

async function trimBackups(db: IDBPDatabase<IncrementusDb>, slot: Slot): Promise<void> {
  const tx = db.transaction('backups', 'readwrite');
  const keys = await tx.store.index('by-slot').getAllKeys(slot);
  const excess = keys.length - MAX_BACKUPS_PER_SLOT;
  for (let i = 0; i < excess; i += 1) {
    const key = keys[i];
    if (key !== undefined) {
      await tx.store.delete(key);
    }
  }
  await tx.done;
}

/** Persist a state to a slot and push a copy onto that slot's backup ring. */
export async function saveToSlot(
  state: GameState,
  savedAt: number,
  slot: Slot = DEFAULT_SLOT,
): Promise<void> {
  const envelope = encodeSave(state, savedAt);
  const db = await openGameDb();
  try {
    await db.put('saves', envelope, slot);
    await db.add('backups', { slot, savedAt, envelope });
    await trimBackups(db, slot);
  } finally {
    db.close();
  }
}

async function recoverFromBackups(
  db: IDBPDatabase<IncrementusDb>,
  slot: Slot,
): Promise<GameState | null> {
  const records = await db.getAllFromIndex('backups', 'by-slot', slot);
  for (let i = records.length - 1; i >= 0; i -= 1) {
    const record = records[i];
    if (!record) continue;
    const decoded = tryDecode(record.envelope);
    if (decoded) return decoded;
  }
  return null;
}

/**
 * Load a slot's state. A missing slot yields a fresh start; a corrupt slot falls back
 * to the newest decodable backup; only if the whole ring is unusable do we start fresh.
 */
export async function loadFromSlot(slot: Slot = DEFAULT_SLOT): Promise<GameState> {
  const db = await openGameDb();
  try {
    const envelope = await db.get('saves', slot);
    if (envelope) {
      const decoded = tryDecode(envelope);
      if (decoded) return decoded;
      const recovered = await recoverFromBackups(db, slot);
      if (recovered) return recovered;
    }
    return createInitialState();
  } finally {
    db.close();
  }
}

/** A loaded save plus the wall-clock stamp it was written at (`null` for a fresh start). */
export interface LoadedSave {
  readonly state: GameState;
  readonly savedAt: number | null;
}

/**
 * Like {@link loadFromSlot}, but also returns the envelope's `savedAt` so the game loop
 * can compute the offline elapsed time (GAM-7). A fresh start (missing/unusable slot)
 * reports `savedAt: null`, which the caller reads as "no absence to catch up".
 */
export async function loadFromSlotWithMeta(
  slot: Slot = DEFAULT_SLOT,
): Promise<LoadedSave> {
  const db = await openGameDb();
  try {
    const envelope = await db.get('saves', slot);
    if (envelope) {
      const decoded = tryDecode(envelope);
      if (decoded) return { state: decoded, savedAt: envelope.savedAt };
      const records = await db.getAllFromIndex('backups', 'by-slot', slot);
      for (let i = records.length - 1; i >= 0; i -= 1) {
        const record = records[i];
        if (!record) continue;
        const recovered = tryDecode(record.envelope);
        if (recovered) return { state: recovered, savedAt: record.savedAt };
      }
    }
    return { state: createInitialState(), savedAt: null };
  } finally {
    db.close();
  }
}

/** The backup ring for a slot, newest first — powers a "restore a backup" UI. */
export async function listBackups(slot: Slot = DEFAULT_SLOT): Promise<BackupRecord[]> {
  const db = await openGameDb();
  try {
    const records = await db.getAllFromIndex('backups', 'by-slot', slot);
    return records.reverse();
  } finally {
    db.close();
  }
}

// ── Settings ──────────────────────────────────────────────────────────────────

export async function getSetting<T>(key: string): Promise<T | undefined> {
  const db = await openGameDb();
  try {
    return (await db.get('settings', key)) as T | undefined;
  } finally {
    db.close();
  }
}

export async function setSetting(key: string, value: unknown): Promise<void> {
  const db = await openGameDb();
  try {
    await db.put('settings', value, key);
  } finally {
    db.close();
  }
}

/**
 * Ask the browser to keep our storage from being evicted under pressure. Called on
 * first meaningful progress. Guarded so it is a no-op (returning `false`) anywhere
 * the Storage API is absent — including Node under tests.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  const storage = (
    globalThis as {
      navigator?: { storage?: { persist?: () => Promise<boolean> } };
    }
  ).navigator?.storage;
  if (!storage?.persist) {
    return false;
  }
  try {
    return await storage.persist();
  } catch {
    return false;
  }
}
